import { useEffect, useState } from 'react';
import { LogoMark } from '@/components/BrandLogo';
import { LOCKUP_PATHS } from '@/components/Logo';

// ─────────────────────────────────────────────────────────────────────────────
// Açılış ekranı — İKİ AŞAMA:
//  1) LOADING: Terminal arka planı tarzında zemin — koyu mavi + eriyen
//     mühendislik ızgarası + çapraz ışık huzmesi + nefes alan ışık alanları +
//     sönük nabız logo.
//  2) INTRO: neon hat kendini çizer; sonra AÇILMA — arka plan, verilen SVG
//     hattının KONTÜRÜ boyunca üst/alt panellere bölünüp çizgilerle senkron
//     yukarı/aşağı ayrılarak siteyi açar.
// prefers-reduced-motion'da hiç oynatılmaz.
// ─────────────────────────────────────────────────────────────────────────────

const CORE = '#dbe8ff';
const NEON = '#2f5bff';
const PANEL_BG =
  'radial-gradient(120% 92% at 50% -8%, #0a1a48 0%, #060e2c 46%, #02050f 100%)';

const TOP_LINE =
  'M0 89.1733L717.593 89.1733C728.805 89.1733 739.268 83.5421 745.444 74.1838L782.857 17.4895C789.032 8.1312 799.495 2.50007 810.708 2.50007H1182.61C1193.83 2.50007 1204.29 8.1312 1210.46 17.4895L1247.88 74.1838C1254.05 83.5421 1264.52 89.1733 1275.73 89.1733L1993.32 89.1733';
const BOT_LINE =
  'M1993.32 122.012L1275.73 122.012C1264.52 122.012 1254.05 127.643 1247.88 137.002L1210.46 193.696C1204.29 203.054 1193.83 208.685 1182.61 208.685H810.708C799.495 208.685 789.032 203.054 782.857 193.696L745.444 137.002C739.268 127.643 728.805 122.012 717.593 122.012L0 122.012';

// clip-path (objectBoundingBox 0..1) — SVG hattı, tam ekrana oranlanmış.
const TOP_CLIP_LINE =
  'M0 0.485 L0.3599 0.485 C0.3655 0.485 0.3708 0.48 0.3739 0.4716 L0.3926 0.4211 C0.3957 0.4128 0.401 0.4078 0.4066 0.4078 L0.5931 0.4078 C0.5987 0.4078 0.6039 0.4128 0.607 0.4211 L0.6258 0.4716 C0.6289 0.48 0.6342 0.485 0.6398 0.485 L1 0.485';
const BOT_CLIP_LINE =
  'M1 0.5143 L0.6398 0.5143 C0.6342 0.5143 0.6289 0.5193 0.6258 0.5276 L0.607 0.5782 C0.6039 0.5865 0.5987 0.5915 0.5931 0.5915 L0.4066 0.5915 C0.401 0.5915 0.3957 0.5865 0.3926 0.5782 L0.3739 0.5276 C0.3708 0.5193 0.3655 0.5143 0.3599 0.5143 L0 0.5143';

// ── ORAN DUYARLI ÖLÇEKLEME ──────────────────────────────────────────────────
// Yollar preserveAspectRatio="none" ile ekrana esnetilir; aynı koordinatlar her
// ekran oranında farklı bir şekle dönüşür. Dikey telefonda yatay ölçek dikeye
// göre ~4 kat küçük kaldığından çentik daralıp derinleşiyordu.
//
// TEK GEOMETRİ + iki çarpan: x ve y AYNI oranda büyür (sizeScale), y ayrıca
// esnetme telafisi alır (depthScale). Sonuç: piksel cinsinden genişlik/derinlik
// oranı HER ekranda masaüstündekiyle aynı — şekil bozulmaz, yalnızca dar
// ekranda ölçeği büyür (çentik genişliğin %28'inden ~%50'sine çıkar).
//
// DİKKAT — denenip başarısız olanlar:
//   • mobile ÖZEL ikinci geometri  → tek breakpoint (767px); 320px telefonla
//     767px tablet aynı şekli alıyordu ve hiçbiri masaüstüne benzemiyordu
//   • sabit y kesri                → dikey telefonda dar ve derin bir V
//   • yalnız derinliği ölçeklemek  → şekil doğru ama dar ekranda düz çizgiye iner
//   • x'i ayrı, oranı ayrı ölçeklemek → şekli BOZAR
const REF_ASPECT = 1994 / 1122; // yolların çizildiği kutunun oranı (16:9)
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const narrowness = (a: number) => clamp01((1.4 - a) / (1.4 - 0.46));
const sizeScale = (a: number) => 1 + narrowness(a) * 0.8;
const depthScale = (a: number) =>
  Math.min(1.3, Math.max(0.22, (a / REF_ASPECT) * sizeScale(a)));

// Yol ölçekleyici.
//   y → hattın taban çizgisine göre (derinlik)
//   x → kutunun merkezine göre (genişlik)
// Kenarlar (x = 0 ve x = xMax) ölçeklenmez: hat kenardan kenara uzanır.
// YAZIM SIRASI DEĞİŞMEZ → çizim yönleri (üst soldan, alt sağdan) korunur.
//
// DİKKAT: "koordinatlar sırayla x,y gelir" varsayımı YETMEZ — bu yollarda H
// (yatay çizgi, YALNIZ x alır) komutu var ve sonrasındaki tüm x/y atamalarını
// kaydırır. Bu yüzden komutlar gerçekten ayrıştırılır ve her komutun koordinat
// imzası kullanılır. (Yollar tamamen MUTLAK komutlardan oluşur.)
const CMD_ARGS: Record<string, ('x' | 'y')[]> = {
  M: ['x', 'y'],
  L: ['x', 'y'],
  T: ['x', 'y'],
  C: ['x', 'y', 'x', 'y', 'x', 'y'],
  S: ['x', 'y', 'x', 'y'],
  Q: ['x', 'y', 'x', 'y'],
  H: ['x'],
  V: ['y'],
  Z: [],
};

const scalePath = (
  d: string,
  baseline: number,
  xMax: number,
  k: number,
  w: number,
) => {
  const cx = xMax / 2;
  const sx = (v: number) => (v <= 0 || v >= xMax ? v : cx + (v - cx) * w);
  const sy = (v: number) => baseline + (v - baseline) * k;
  const out: string[] = [];
  let sig: ('x' | 'y')[] = [];
  let i = 0;
  const re = /([A-Za-z])|(-?\d*\.?\d+(?:e[-+]?\d+)?)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(d)) !== null) {
    if (m[1]) {
      sig = CMD_ARGS[m[1].toUpperCase()] ?? [];
      i = 0;
      out.push(m[1]);
    } else {
      const v = Number(m[2]);
      const kind = sig.length ? sig[i % sig.length] : 'x';
      i += 1;
      out.push(String(+(kind === 'y' ? sy(v) : sx(v)).toFixed(5)));
    }
  }
  return out.join(' ');
};

// Hatların taban y'leri ve kutu genişlikleri (iki ayrı koordinat uzayı):
//   çizilen hat → 1994×1122 kutusu
//   clip-path   → objectBoundingBox 0..1
// Kapsül BOŞLUĞUNUN ölçüleri (0..1 uzayı) — logo bunun içine sığdırılır.
const FLAT_DX = 0.1865; // çentiğin düz taban genişliği
const GAP_DY = 0.0293; // iki hattın taban çizgileri arası
const NOTCH_DY = 0.0772; // tek hattın derinliği
const LOGO_RATIO = 286 / 73; // marka kilidinin en/boy oranı

const LINE_XMAX = 1993.32;
const LINE_Y_TOP = 89.1733;
const LINE_Y_BOT = 122.012;
const CLIP_Y_TOP = 0.485;
const CLIP_Y_BOT = 0.5143;

// MID_CLIP iki taban çizgisini birden içerir → üst ve alt parçası AYRI ayrı
// ölçeklenip birleştirilir.
const MID_TOP =
  'M0 0.485 L0.3599 0.485 C0.3655 0.485 0.3708 0.48 0.3739 0.4716 L0.3926 0.4211 C0.3957 0.4128 0.401 0.4078 0.4066 0.4078 L0.5931 0.4078 C0.5987 0.4078 0.6039 0.4128 0.607 0.4211 L0.6258 0.4716 C0.6289 0.48 0.6342 0.485 0.6398 0.485 L1 0.485';
const MID_BOT =
  'L1 0.5143 L0.6398 0.5143 C0.6342 0.5143 0.6289 0.5193 0.6258 0.5276 L0.607 0.5782 C0.6039 0.5865 0.5987 0.5915 0.5931 0.5915 L0.4066 0.5915 C0.401 0.5915 0.3957 0.5865 0.3926 0.5782 L0.3739 0.5276 C0.3708 0.5193 0.3655 0.5143 0.3599 0.5143 L0 0.5143';

// Terminal/Eklem/NextLevel backdrop dili (koyu, mavi ton): eriyen mühendislik
// ızgarası + nefes alan ışık alanları + silik Kapsül amblemi silüetleri + vinyet.
const GRID_MASK =
  'radial-gradient(75% 65% at 50% 32%, #000 25%, transparent 100%)';
const TerminalBg = () => (
  <>
    {/* İnce mühendislik ızgarası — merkezde görünür, kenarlarda erir */}
    <div
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage:
          'linear-gradient(rgba(90,150,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(90,150,255,0.045) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
        maskImage: GRID_MASK,
        WebkitMaskImage: GRID_MASK,
      }}
    />
    {/* Nefes alan ışık alanları (koyu) */}
    <div
      className="pointer-events-none absolute left-1/2 top-[-12%] h-[58vmin] w-[90vmin] -translate-x-1/2 rounded-full"
      style={{
        background:
          'radial-gradient(closest-side, rgba(45,105,220,0.14), transparent)',
        filter: 'blur(30px)',
        animation: 'ktp-breathe 11s ease-in-out infinite',
      }}
    />
    <div
      className="pointer-events-none absolute bottom-[-16%] right-[-10%] h-[62vmin] w-[62vmin] rounded-full"
      style={{
        background:
          'radial-gradient(closest-side, rgba(30,70,160,0.13), transparent)',
        filter: 'blur(34px)',
        animation: 'ktp-breathe 14s ease-in-out 2s infinite',
      }}
    />
    <div
      className="pointer-events-none absolute left-[-12%] top-[42%] h-[52vmin] w-[52vmin] rounded-full"
      style={{
        background:
          'radial-gradient(closest-side, rgba(96,64,180,0.09), transparent)',
        filter: 'blur(30px)',
        animation: 'ktp-breathe 17s ease-in-out 5s infinite',
      }}
    />
    {/* Silik Kapsül amblemi silüetleri — hafif süzülen */}
    <div
      className="pointer-events-none absolute right-[-3%] top-[8%] w-[30vmin]"
      style={{ opacity: 0.06, animation: 'ktp-drift 14s ease-in-out infinite' }}
    >
      <LogoMark className="w-full text-[#5f9bff]" />
    </div>
    <div
      className="pointer-events-none absolute bottom-[5%] left-[-3%] w-[36vmin]"
      style={{
        opacity: 0.05,
        animation: 'ktp-drift 17s ease-in-out 3s infinite',
      }}
    >
      <LogoMark className="w-full text-[#8ea8ff]" />
    </div>
    {/* Üstten hafif vinyet */}
    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-40"
      style={{
        background:
          'linear-gradient(to bottom, rgba(3,6,17,0.72), transparent)',
      }}
    />
  </>
);

const KEYFRAMES = `
  @keyframes ktp-logo { 0%,100% { opacity: .38; transform: scale(0.97); } 50% { opacity: .72; transform: scale(1); } }
  @keyframes ktp-breathe { 0%,100% { opacity: .5; } 50% { opacity: 1; } }
  @keyframes ktp-drift { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-16px); } }
`;

const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

type Phase = 'loading' | 'intro' | 'gone';

// Perde hiçbir koşulda bundan uzun sürmesin (içerik hiç gelmese bile).
const HARD_CAP = 5200;

const isCompactSplashDevice = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(max-width: 1023px), (pointer: coarse)').matches;

const IntroSplash = ({ waitForFrame = false }: { waitForFrame?: boolean }) => {
  const [active] = useState(
    () => typeof window !== 'undefined' && !prefersReduced(),
  );
  const [phase, setPhase] = useState<Phase>('loading');
  const [compactSplash, setCompactSplash] = useState(isCompactSplashDevice);
  // Şekil, tek bir breakpoint yerine EKRAN ORANINA göre sürekli ölçeklenir →
  // 320px telefondan tablete kadar her cihaz kendi doğru şeklini alır.
  // Logo kapsül boşluğunun İÇİNE oturtulacağı için oran yetmez; gerçek piksel
  // ölçüsü tutulur. Ölçüm YALNIZCA 'loading' aşamasında yapılır, intro
  // başlayınca DONAR: perde body.overflow'u kapattığında (ve mobilde adres
  // çubuğu gizlendiğinde) resize tetikleniyor; animasyon sürerken yeniden
  // ölçmek clip-path'leri baştan hesaplatıp geçişi bozardı.
  const [vp, setVp] = useState(() =>
    typeof window === 'undefined'
      ? { w: 1994, h: 1122 }
      : { w: window.innerWidth, h: window.innerHeight },
  );
  const aspect = vp.w / vp.h;
  useEffect(() => {
    if (phase !== 'loading') return;
    const on = () =>
      setVp((prev) =>
        prev.w === window.innerWidth && prev.h === window.innerHeight
          ? prev
          : { w: window.innerWidth, h: window.innerHeight },
      );
    on();
    window.addEventListener('resize', on);
    window.addEventListener('orientationchange', on);
    return () => {
      window.removeEventListener('resize', on);
      window.removeEventListener('orientationchange', on);
    };
  }, [phase]);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 1023px), (pointer: coarse)');
    const update = () => setCompactSplash(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  const [drawn, setDrawn] = useState(false);
  const [opening, setOpening] = useState(false); // panellere geçilir, tek zemin gizlenir
  const [slide, setSlide] = useState(false); // paneller ayrılır (kayar)

  // Perde, SAYFA İÇERİĞİ ve ana sayfa frame sahnesi hazır olana kadar durur.
  //
  // window 'load' özellikle mobilde görsel/PDF/arka plan gibi kritik olmayan
  // kaynakları bekleyip açılış perdesini gereksiz uzatabiliyor. Bu yüzden hazır
  // olma sinyali React içeriği + minimum süre ile sınırlı: sayfa gerçekten
  // çizilince perde açılır, dekoratif kaynaklar arkadan devam eder.
  //
  // Layout'lar iki işaret koyar: [data-page-content] = sayfa çizildi,
  // [data-page-loading] = TAM EKRAN yer tutucu hâlâ ekranda. Hazır olma
  // koşulu: içerik işareti VAR ve tam ekran yer tutucu YOK.
  useEffect(() => {
    if (!active) return;
    const isPageReady = () =>
      !!document.querySelector('[data-page-content]') &&
      !document.querySelector('[data-page-loading]');
    const isFrameReady = () =>
      !waitForFrame || !!document.querySelector('[data-home-frame-ready]');

    let minDone = false;
    let pageReady = isPageReady();
    let frameReady = isFrameReady();
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      mo?.disconnect();
      window.removeEventListener('ktp-home-frame-ready', onFrameReady);
      setPhase('intro');
    };
    const tryStart = () => {
      if (minDone && pageReady && frameReady) start();
    };
    const minDelay = compactSplash ? 120 : 900;
    const minT = window.setTimeout(() => {
      minDone = true;
      tryStart();
    }, minDelay);

    // İşaretler React tarafından sonradan eklenip kaldırıldığı için DOM izlenir.
    let mo: MutationObserver | undefined;
    const onFrameReady = () => {
      frameReady = true;
      tryStart();
    };
    if (!frameReady)
      window.addEventListener('ktp-home-frame-ready', onFrameReady);

    if (!pageReady || !frameReady) {
      mo = new MutationObserver(() => {
        if (isPageReady()) pageReady = true;
        if (isFrameReady()) frameReady = true;
        if (pageReady && frameReady) mo?.disconnect();
        tryStart();
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }

    const cap = window.setTimeout(start, HARD_CAP);
    return () => {
      window.clearTimeout(minT);
      window.clearTimeout(cap);
      window.removeEventListener('ktp-home-frame-ready', onFrameReady);
      mo?.disconnect();
    };
  }, [active, compactSplash, waitForFrame]);

  useEffect(() => {
    if (phase !== 'intro') return;
    if (compactSplash) {
      setOpening(false);
      setSlide(false);
      const timer = window.setTimeout(() => setPhase('gone'), 460);
      return () => window.clearTimeout(timer);
    }
    const timers = [
      window.setTimeout(() => setDrawn(true), 60),
      window.setTimeout(() => setOpening(true), 2450),
      window.setTimeout(() => setPhase('gone'), 3550),
    ];
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [compactSplash, phase]);

  // Paneller "rest" konumda mount olduktan SONRA kaydır (böylece geçiş kesintisiz
  // ve başta kontür dikişi görünmez).
  useEffect(() => {
    if (!opening) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSlide(true));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [opening]);

  useEffect(() => {
    if (!active || phase === 'gone') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active, phase]);

  if (!active || phase === 'gone') return null;

  const bright = {
    stroke: CORE,
    strokeWidth: 3.5,
    fill: 'none' as const,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    vectorEffect: 'non-scaling-stroke' as const,
    pathLength: 1,
    strokeDasharray: 1,
    strokeDashoffset: drawn ? 0 : 1,
    style: {
      transition: 'stroke-dashoffset 1.15s cubic-bezier(0.45,0,0.2,1)',
      filter: `drop-shadow(0 0 4px ${NEON}) drop-shadow(0 0 12px ${NEON}99)`,
      willChange: 'stroke-dashoffset',
    },
  };
  const trans = 'transform 0.9s cubic-bezier(0.76,0,0.24,1)';
  // ── Geometri — TEK kutu, TEK yol seti; yalnızca ölçek orana göre değişir.
  // vb / restY / upY / downY ANİMASYON parametreleridir ve SABİTTİR: böylece
  // kayma animasyonu her ekranda masaüstündekiyle birebir aynı kalır.
  const k = depthScale(aspect);
  const w = sizeScale(aspect);

  // ── Logo ölçüsü KAPSÜLDEN türetilir ───────────────────────────────────────
  // Sabit `clamp(150px, 44vw, 300px)` çentikten bağımsızdı; çentik orana göre
  // değişirken logo değişmediğinden masaüstü DIŞINDA her cihazda kapsülü aşıp
  // hatların üzerine biniyordu (telefonda 38-48px).
  const capsuleW = FLAT_DX * w * vp.w; // düz taban genişliği
  const capsuleH = (GAP_DY + 2 * NOTCH_DY * k) * vp.h; // iki hat arası açıklık
  // Hem genişlikten hem yükseklikten sınırla; %84 iç pay bırakır.
  const logoW = Math.min(capsuleW, capsuleH * LOGO_RATIO) * 0.84;
  const vb = '0 0 1994 1122';
  const restY = 455;
  const upY = -667;
  const downY = 1577;
  // Çizilen hatlar (1994×1122 uzayı) — yön korunur: üst soldan, alt sağdan.
  const topD = scalePath(TOP_LINE, LINE_Y_TOP, LINE_XMAX, k, w);
  const botD = scalePath(BOT_LINE, LINE_Y_BOT, LINE_XMAX, k, w);
  // Panel konturları (objectBoundingBox 0..1 uzayı)
  const topClip = `${scalePath(TOP_CLIP_LINE, CLIP_Y_TOP, 1, k, w)} L1 0 L0 0 Z`;
  const botClip = `${scalePath(BOT_CLIP_LINE, CLIP_Y_BOT, 1, k, w)} L0 1 L1 1 Z`;
  const midClip = `${scalePath(MID_TOP, CLIP_Y_TOP, 1, k, w)} ${scalePath(
    MID_BOT,
    CLIP_Y_BOT,
    1,
    k,
    w,
  )} Z`;
  const compactLogoW = Math.max(150, Math.min(logoW, 260));

  // data-intro-splash: perde ekranda işareti — açılış popup'ları (SitePopups)
  // perde kalkana kadar bekler.
  if (compactSplash) {
    return (
      <div
        data-intro-splash
        className="fixed inset-0 z-[9999] overflow-hidden"
        style={{
          background: PANEL_BG,
          opacity: phase === 'intro' ? 0 : 1,
          pointerEvents: phase === 'intro' ? 'none' : 'auto',
          transition: 'opacity 0.42s ease',
        }}
        aria-hidden
      >
        <style>{KEYFRAMES}</style>
        <TerminalBg />
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div
            className="relative flex items-center justify-center"
            style={{ animation: 'ktp-logo 1.9s ease-in-out infinite' }}
          >
            <div
              className="absolute rounded-full"
              style={{
                width: compactLogoW * 1.9,
                height: compactLogoW * 0.65,
                background: `radial-gradient(closest-side, ${NEON}33, transparent)`,
                filter: 'blur(26px)',
              }}
            />
            <svg
              viewBox="0 0 286 73"
              fill="currentColor"
              role="img"
              aria-label="Kapsül Teknoloji Platformu"
              className="relative text-white"
              style={{ width: compactLogoW }}
            >
              {LOCKUP_PATHS.map((d, i) => (
                <path key={i} d={d} />
              ))}
            </svg>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-intro-splash
      className="fixed inset-0 z-[9999] overflow-hidden"
      style={{ pointerEvents: opening ? 'none' : 'auto' }}
      aria-hidden
    >
      <style>{KEYFRAMES}</style>

      {/* clip-path tanımları (hattın kontürü) */}
      <svg width="0" height="0" className="absolute" aria-hidden>
        <defs>
          <clipPath id="ktp-top" clipPathUnits="objectBoundingBox">
            <path d={topClip} />
          </clipPath>
          <clipPath id="ktp-bot" clipPathUnits="objectBoundingBox">
            <path d={botClip} />
          </clipPath>
          <clipPath id="ktp-mid" clipPathUnits="objectBoundingBox">
            <path d={midClip} />
          </clipPath>
        </defs>
      </svg>

      {/* Açılıştan ÖNCE: TEK PARÇA arka plan (kırpma yok → başta kontür çizgisi
          oluşmaz, yalnızca neon kendini çizerek belirir) */}
      {!opening && (
        <div className="absolute inset-0" style={{ background: PANEL_BG }}>
          <TerminalBg />
        </div>
      )}

      {/* Açılış: kontür boyunca üç panel (çizgilerle senkron ayrılır) */}
      {opening && (
        <>
          <div
            className="absolute inset-0"
            style={{
              background: PANEL_BG,
              clipPath: 'url(#ktp-top)',
              transform: slide ? 'translateY(-100%)' : 'translateY(0)',
              transition: trans,
              willChange: 'transform',
            }}
          >
            <TerminalBg />
          </div>
          <div
            className="absolute inset-0"
            style={{
              background: PANEL_BG,
              clipPath: 'url(#ktp-bot)',
              transform: slide ? 'translateY(100%)' : 'translateY(0)',
              transition: trans,
              willChange: 'transform',
            }}
          >
            <TerminalBg />
          </div>
          <div
            className="absolute inset-0"
            style={{
              background: PANEL_BG,
              clipPath: 'url(#ktp-mid)',
              opacity: slide ? 0 : 1,
              transition: 'opacity 0.4s ease',
            }}
          >
            <TerminalBg />
          </div>
        </>
      )}

      {/* Neon hatlar (kırpılmaz) — panellerle SENKRON kayar; INTRO'da çizilir */}
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox={vb}
        preserveAspectRatio="none"
        fill="none"
      >
        <g
          style={{
            transform: `translateY(${slide ? upY : restY}px)`,
            transition: trans,
            willChange: 'transform',
          }}
        >
          {phase === 'intro' && <path d={topD} {...bright} />}
        </g>
        <g
          style={{
            transform: `translateY(${slide ? downY : restY}px)`,
            transition: trans,
            willChange: 'transform',
          }}
        >
          {phase === 'intro' && <path d={botD} {...bright} />}
        </g>
      </svg>

      {/* Merkez — logo (yüklemede sönük nabız, intro'da parlak, açılışta solar) */}
      <div
        className="pointer-events-none absolute inset-0 flex items-center justify-center"
        style={{ opacity: opening ? 0 : 1, transition: 'opacity 0.4s ease' }}
      >
        <div
          className="relative flex items-center justify-center"
          style={
            phase === 'intro'
              ? {
                  opacity: 1,
                  transform: 'scale(1)',
                  transition:
                    'opacity 0.6s ease, transform 0.7s cubic-bezier(0.34,1.56,0.64,1)',
                }
              : { animation: 'ktp-logo 1.9s ease-in-out infinite' }
          }
        >
          <div
            className="absolute rounded-full"
            style={{
              // Mobilde vw tabanlı ölçü haleyi yok ediyordu; clamp ile dar
              // ekranda görünür, geniş ekranda eski boyutunda kalır.
              width: logoW * 1.9,
              height: logoW * 0.65,
              background: `radial-gradient(closest-side, ${NEON}33, transparent)`,
              filter: 'blur(26px)',
            }}
          />
          {/* Marka kilidi — NAVBAR'daki ile aynı vektör: "KAPSÜL" yazısı SOLDA,
              "K" işareti sağda (eski /kapsul-logo-tr.svg işareti solda tutuyordu). */}
          <svg
            viewBox="0 0 286 73"
            fill="currentColor"
            role="img"
            aria-label="Kapsül Teknoloji Platformu"
            className="relative text-white"
            style={{ width: logoW }}
          >
            {LOCKUP_PATHS.map((d, i) => (
              <path key={i} d={d} />
            ))}
          </svg>
        </div>
      </div>
    </div>
  );
};

export default IntroSplash;
